import { gerarUuid } from "@/shared/http/gerar-uuid";
import {
  comentarioReferenciaSchema,
  colecaoDisponivelSchema,
  colecaoSchema,
  contaSeguidaSchema,
  contaStoriesMonitoradaSchema,
  estadoSincronizacaoSchema,
  excluirComentarioReferenciaRespostaSchema,
  invalidarInstagramSessaoRespostaSchema,
  iniciarSincronizacaoRespostaSchema,
  instagramSessaoSchema,
  conectarInstagramRespostaSchema,
  paginaDeReferenciasSchema,
  paginaDeStoriesSchema,
  salvarColecoesSelecionadasRespostaSchema,
  salvarContasStoriesSelecionadasRespostaSchema,
  statusIntegracoesSchema,
  type AtualizarClickUp,
  type ComentarioReferencia,
  type Colecao,
  type ColecaoDisponivel,
  type ContaSeguida,
  type ContaStoriesMonitorada,
  type EstadoSincronizacao,
  type ExcluirComentarioReferenciaResposta,
  type IniciarSincronizacaoResposta,
  type InstagramSessao,
  type InvalidarInstagramSessaoResposta,
  type ConectarInstagramResposta,
  type ModoSincronizacaoReferencias,
  type OrdenacaoDaGaleria,
  type OrdenacaoDeStories,
  type PaginaDeReferencias,
  type PaginaDeStories,
  type Referencia,
  type SalvarColecoesSelecionadasResposta,
  type SalvarContasStoriesSelecionadasResposta,
  type StatusIntegracoes,
  type Story,
  referenciaSchema,
  storySchema,
} from "@base/contracts";
import type { ClienteHttp } from "../../../shared/http/cliente-http";

export type FiltroDeReferencias = {
  readonly limite?: number;
  readonly cursor?: string;
  readonly collectionId?: string;
  readonly q?: string;
  readonly ordenar?: OrdenacaoDaGaleria;
  readonly isAd?: boolean;
  readonly soFavoritos?: boolean;
};

export type FiltroDeStories = {
  readonly limite?: number;
  readonly cursor?: string;
  readonly contaIds?: readonly string[];
  readonly q?: string;
  readonly mediaType?: "image" | "video";
  readonly ordenar?: OrdenacaoDeStories;
  readonly soFavoritos?: boolean;
};

/**
 * Porta de saida do modulo no front (o equivalente ao repositorio na API).
 */
export interface ReferenciasGateway {
  listar(filtro: FiltroDeReferencias): Promise<PaginaDeReferencias>;
  listarColecoes(): Promise<readonly Colecao[]>;
  listarColecoesDisponiveis(forcar?: boolean): Promise<readonly ColecaoDisponivel[]>;
  salvarColecoesSelecionadas(
    ids: readonly string[],
  ): Promise<SalvarColecoesSelecionadasResposta>;
  listarContasSeguidas(opcoes?: {
    readonly forcar?: boolean;
    readonly q?: string;
  }): Promise<readonly ContaSeguida[]>;
  salvarContasStoriesSelecionadas(
    ids: readonly string[],
  ): Promise<SalvarContasStoriesSelecionadasResposta>;
  listarStories(filtro: FiltroDeStories): Promise<PaginaDeStories>;
  listarContasStoriesMonitoradas(): Promise<readonly ContaStoriesMonitorada[]>;
  statusSincronizacaoStories(): Promise<EstadoSincronizacao>;
  sincronizarStories(): Promise<IniciarSincronizacaoResposta>;
  statusSincronizacao(): Promise<EstadoSincronizacao>;
  sincronizar(modo?: ModoSincronizacaoReferencias): Promise<IniciarSincronizacaoResposta>;
  statusSessaoInstagram(): Promise<InstagramSessao>;
  statusIntegracoes(): Promise<StatusIntegracoes>;
  atualizarClickUp(entrada: AtualizarClickUp): Promise<StatusIntegracoes>;
  validarSessaoInstagram(): Promise<InstagramSessao>;
  invalidarSessaoInstagram(): Promise<InvalidarInstagramSessaoResposta>;
  conectarInstagram(entrada: {
    readonly username: string;
    readonly password: string;
  }): Promise<ConectarInstagramResposta>;
  confirmar2faInstagram(entrada: {
    readonly desafioId: string;
    readonly codigo: string;
  }): Promise<ConectarInstagramResposta>;
  importarSessaoInstagram(sessao: Record<string, unknown>): Promise<InstagramSessao>;
  listarComentarios(
    postId: string,
  ): Promise<readonly ComentarioReferencia[]>;
  criarComentario(
    postId: string,
    entrada: { texto: string; autorNome: string },
  ): Promise<ComentarioReferencia>;
  atualizarComentario(
    postId: string,
    comentarioId: string,
    texto: string,
  ): Promise<ComentarioReferencia>;
  excluirComentario(
    postId: string,
    comentarioId: string,
  ): Promise<ExcluirComentarioReferenciaResposta>;
  atualizarFlagsPost(entrada: {
    readonly id: string;
    readonly favorito?: boolean;
    readonly oculto?: boolean;
  }): Promise<Referencia>;
  atualizarFlagsStory(entrada: {
    readonly id: string;
    readonly favorito?: boolean;
    readonly oculto?: boolean;
  }): Promise<Story>;
}

/** Implementacao HTTP. Unico arquivo do front que conhece os caminhos da API. */
export class ReferenciasGatewayHttp implements ReferenciasGateway {
  constructor(private readonly http: ClienteHttp) {}

  async listar(filtro: FiltroDeReferencias): Promise<PaginaDeReferencias> {
    return this.http.requisitar("/v1/referencias", paginaDeReferenciasSchema, {
      consulta: {
        ...(filtro.limite !== undefined ? { limite: filtro.limite } : {}),
        ...(filtro.cursor !== undefined ? { cursor: filtro.cursor } : {}),
        ...(filtro.collectionId !== undefined
          ? { collectionId: filtro.collectionId }
          : {}),
        ...(filtro.q !== undefined ? { q: filtro.q } : {}),
        ...(filtro.ordenar !== undefined ? { ordenar: filtro.ordenar } : {}),
        ...(filtro.isAd !== undefined
          ? { isAd: filtro.isAd ? "true" : "false" }
          : {}),
        ...(filtro.soFavoritos ? { favoritos: "true" } : {}),
      },
    });
  }

  async listarColecoes(): Promise<readonly Colecao[]> {
    return this.http.requisitar(
      "/v1/referencias/colecoes",
      colecaoSchema.array(),
    );
  }

  async listarColecoesDisponiveis(
    forcar = false,
  ): Promise<readonly ColecaoDisponivel[]> {
    return this.http.requisitar(
      "/v1/referencias/colecoes/disponiveis",
      colecaoDisponivelSchema.array(),
      forcar ? { consulta: { forcar: "true" } } : {},
    );
  }

  async salvarColecoesSelecionadas(
    ids: readonly string[],
  ): Promise<SalvarColecoesSelecionadasResposta> {
    return this.http.requisitar(
      "/v1/referencias/colecoes/selecionadas",
      salvarColecoesSelecionadasRespostaSchema,
      {
        metodo: "PATCH",
        corpo: { ids },
        chaveDeIdempotencia: gerarUuid(),
      },
    );
  }

  async listarContasSeguidas(opcoes?: {
    readonly forcar?: boolean;
    readonly q?: string;
  }): Promise<readonly ContaSeguida[]> {
    return this.http.requisitar(
      "/v1/referencias/instagram/seguindo",
      contaSeguidaSchema.array(),
      {
        consulta: {
          ...(opcoes?.forcar ? { forcar: "true" } : {}),
          ...(opcoes?.q?.trim() ? { q: opcoes.q.trim() } : {}),
        },
      },
    );
  }

  async salvarContasStoriesSelecionadas(
    ids: readonly string[],
  ): Promise<SalvarContasStoriesSelecionadasResposta> {
    return this.http.requisitar(
      "/v1/referencias/instagram/stories/selecionadas",
      salvarContasStoriesSelecionadasRespostaSchema,
      {
        metodo: "PATCH",
        corpo: { ids },
        chaveDeIdempotencia: gerarUuid(),
      },
    );
  }

  async listarStories(filtro: FiltroDeStories): Promise<PaginaDeStories> {
    return this.http.requisitar(
      "/v1/referencias/stories",
      paginaDeStoriesSchema,
      {
        consulta: {
          ...(filtro.limite !== undefined ? { limite: filtro.limite } : {}),
          ...(filtro.cursor !== undefined ? { cursor: filtro.cursor } : {}),
          ...(filtro.contaIds && filtro.contaIds.length > 0
            ? { contaIds: filtro.contaIds.join(",") }
            : {}),
          ...(filtro.q !== undefined ? { q: filtro.q } : {}),
          ...(filtro.mediaType !== undefined
            ? { mediaType: filtro.mediaType }
            : {}),
          ...(filtro.ordenar !== undefined ? { ordenar: filtro.ordenar } : {}),
          ...(filtro.soFavoritos ? { favoritos: "true" } : {}),
        },
      },
    );
  }

  async listarContasStoriesMonitoradas(): Promise<
    readonly ContaStoriesMonitorada[]
  > {
    return this.http.requisitar(
      "/v1/referencias/stories/contas",
      contaStoriesMonitoradaSchema.array(),
    );
  }

  async statusSincronizacaoStories(): Promise<EstadoSincronizacao> {
    return this.http.requisitar(
      "/v1/referencias/stories/sincronizacao",
      estadoSincronizacaoSchema,
    );
  }

  async sincronizarStories(): Promise<IniciarSincronizacaoResposta> {
    return this.http.requisitar(
      "/v1/referencias/stories/sincronizacao",
      iniciarSincronizacaoRespostaSchema,
      {
        metodo: "POST",
        chaveDeIdempotencia: gerarUuid(),
      },
    );
  }

  async statusSincronizacao(): Promise<EstadoSincronizacao> {
    return this.http.requisitar(
      "/v1/referencias/sincronizacao",
      estadoSincronizacaoSchema,
    );
  }

  async sincronizar(
    modo: ModoSincronizacaoReferencias = "completo",
  ): Promise<IniciarSincronizacaoResposta> {
    return this.http.requisitar(
      "/v1/referencias/sincronizacao",
      iniciarSincronizacaoRespostaSchema,
      {
        metodo: "POST",
        corpo: { modo },
        chaveDeIdempotencia: gerarUuid(),
      },
    );
  }

  async statusSessaoInstagram(): Promise<InstagramSessao> {
    return this.http.requisitar(
      "/v1/referencias/instagram/sessao",
      instagramSessaoSchema,
    );
  }

  async statusIntegracoes(): Promise<StatusIntegracoes> {
    return this.http.requisitar(
      "/v1/referencias/integracoes",
      statusIntegracoesSchema,
    );
  }

  async atualizarClickUp(entrada: AtualizarClickUp): Promise<StatusIntegracoes> {
    return this.http.requisitar(
      "/v1/referencias/integracoes/clickup",
      statusIntegracoesSchema,
      {
        metodo: "PATCH",
        corpo: entrada,
        chaveDeIdempotencia: gerarUuid(),
      },
    );
  }

  async validarSessaoInstagram(): Promise<InstagramSessao> {
    return this.http.requisitar(
      "/v1/referencias/instagram/sessao/validar",
      instagramSessaoSchema,
      {
        metodo: "POST",
        chaveDeIdempotencia: gerarUuid(),
      },
    );
  }

  async invalidarSessaoInstagram(): Promise<InvalidarInstagramSessaoResposta> {
    return this.http.requisitar(
      "/v1/referencias/instagram/sessao",
      invalidarInstagramSessaoRespostaSchema,
      {
        metodo: "DELETE",
        chaveDeIdempotencia: gerarUuid(),
      },
    );
  }

  async conectarInstagram(entrada: {
    readonly username: string;
    readonly password: string;
  }): Promise<ConectarInstagramResposta> {
    return this.http.requisitar(
      "/v1/referencias/instagram/conectar",
      conectarInstagramRespostaSchema,
      {
        metodo: "POST",
        corpo: {
          username: entrada.username,
          password: entrada.password,
        },
        chaveDeIdempotencia: gerarUuid(),
      },
    );
  }

  async confirmar2faInstagram(entrada: {
    readonly desafioId: string;
    readonly codigo: string;
  }): Promise<ConectarInstagramResposta> {
    return this.http.requisitar(
      "/v1/referencias/instagram/conectar/2fa",
      conectarInstagramRespostaSchema,
      {
        metodo: "POST",
        corpo: {
          desafioId: entrada.desafioId,
          codigo: entrada.codigo,
        },
        chaveDeIdempotencia: gerarUuid(),
      },
    );
  }

  async importarSessaoInstagram(
    sessao: Record<string, unknown>,
  ): Promise<InstagramSessao> {
    return this.http.requisitar(
      "/v1/referencias/instagram/sessao/importar",
      instagramSessaoSchema,
      {
        metodo: "POST",
        corpo: { sessao },
        chaveDeIdempotencia: gerarUuid(),
      },
    );
  }

  async listarComentarios(
    postId: string,
  ): Promise<readonly ComentarioReferencia[]> {
    return this.http.requisitar(
      `/v1/referencias/${encodeURIComponent(postId)}/comentarios`,
      comentarioReferenciaSchema.array(),
    );
  }

  async criarComentario(
    postId: string,
    entrada: { texto: string; autorNome: string },
  ): Promise<ComentarioReferencia> {
    return this.http.requisitar(
      `/v1/referencias/${encodeURIComponent(postId)}/comentarios`,
      comentarioReferenciaSchema,
      {
        metodo: "POST",
        corpo: { texto: entrada.texto, autorNome: entrada.autorNome },
        chaveDeIdempotencia: gerarUuid(),
      },
    );
  }

  async atualizarComentario(
    postId: string,
    comentarioId: string,
    texto: string,
  ): Promise<ComentarioReferencia> {
    return this.http.requisitar(
      `/v1/referencias/${encodeURIComponent(postId)}/comentarios/${encodeURIComponent(comentarioId)}`,
      comentarioReferenciaSchema,
      {
        metodo: "PATCH",
        corpo: { texto },
        chaveDeIdempotencia: gerarUuid(),
      },
    );
  }

  async excluirComentario(
    postId: string,
    comentarioId: string,
  ): Promise<ExcluirComentarioReferenciaResposta> {
    return this.http.requisitar(
      `/v1/referencias/${encodeURIComponent(postId)}/comentarios/${encodeURIComponent(comentarioId)}`,
      excluirComentarioReferenciaRespostaSchema,
      {
        metodo: "DELETE",
        chaveDeIdempotencia: gerarUuid(),
      },
    );
  }

  async atualizarFlagsPost(entrada: {
    readonly id: string;
    readonly favorito?: boolean;
    readonly oculto?: boolean;
  }): Promise<Referencia> {
    return this.http.requisitar(
      `/v1/referencias/${encodeURIComponent(entrada.id)}`,
      referenciaSchema,
      {
        metodo: "PATCH",
        corpo: {
          ...(entrada.favorito !== undefined
            ? { favorito: entrada.favorito }
            : {}),
          ...(entrada.oculto !== undefined ? { oculto: entrada.oculto } : {}),
        },
        chaveDeIdempotencia: gerarUuid(),
      },
    );
  }

  async atualizarFlagsStory(entrada: {
    readonly id: string;
    readonly favorito?: boolean;
    readonly oculto?: boolean;
  }): Promise<Story> {
    return this.http.requisitar(
      `/v1/referencias/stories/${encodeURIComponent(entrada.id)}`,
      storySchema,
      {
        metodo: "PATCH",
        corpo: {
          ...(entrada.favorito !== undefined
            ? { favorito: entrada.favorito }
            : {}),
          ...(entrada.oculto !== undefined ? { oculto: entrada.oculto } : {}),
        },
        chaveDeIdempotencia: gerarUuid(),
      },
    );
  }
}
